/**
 * BSG API Hub Profile Service
 * 
 * Fetches and caches the API Hub-specific profile data from APIhub/PostLoginAPIhubProfile.
 * Called after PostLoginCRMCredentials returns, passing the ContactId.
 * Data is consumed by bsgDashboard, bsgAccount, and bsgBilling forms.
 */

import { Logger } from '@/services/Logger';
import { getSiteKey } from '@/services/api/TriSysWebAPI';

const logger = Logger.scope('BSGProfileService');

const STORAGE_KEY = 'bsg_apihub_profile';

// ===== Response Types (maps to VB.Net CPostLoginAPIhubProfileResponse) =====

export interface BSGRecentAPIActivity {
  Timestamp: string;
  APIName: string;
  Endpoint: string;
  Status: number;
  Tokens: number;
}

export interface BSGAPITransaction {
  Timestamp: string;
  Description: string;
  Tokens: number;
  Amount: string;
  Status: string;
}

export interface BSGAPIHubProfile {
  Success: boolean;
  ErrorMessage: string | null;

  // User details (from APIhub profile, NOT trisys_post_login_creds)
  FullName: string;
  CompanyName: string;
  WorkEMailAddress: string;
  WorkPhoneNumber: string;
  PhotoURL: string;

  // Dashboard
  APICallsToday: number;
  TokenBalanceToday: number;
  ActiveAPICount: number;
  APIStatus: string;
  RecentAPIActivity: BSGRecentAPIActivity[];

  // Billing & Tokens
  TransactionHistory: BSGAPITransaction[];

  // Account Settings
  APIKey: string;
  AllowedIPAddresses: string[];
  LowTokenBalanceAlert: boolean;
  WeeklyUsageSummary: boolean;
  NewAPIAnnouncements: boolean;
}

class BSGProfileServiceImpl {
  private profile: BSGAPIHubProfile | null = null;
  private cachedContactId: number | null = null;

  /**
   * Get the contactId that was used to load the profile.
   */
  public getContactId(): number | null {
    return this.cachedContactId;
  }

  /**
   * Fetch the API Hub profile from the server.
   * Pass the ContactId from CRMContact after PostLoginCRMCredentials.
   */
  public async load(contactId: number): Promise<BSGAPIHubProfile | null> {
    this.cachedContactId = contactId;
    const dataServicesKey = sessionStorage.getItem('trisys_data_services_key');
    if (!dataServicesKey) {
      logger.warn('Cannot load BSG profile — no DataServicesKey');
      return null;
    }

    const apiBase = sessionStorage.getItem('trisys_api_endpoint') || 'https://api.trisys.co.uk';
    const siteKey = getSiteKey();

    try {
      logger.debug('Fetching APIhub/PostLoginAPIhubProfile', { contactId });

      const response = await fetch(`${apiBase}/APIhub/PostLoginAPIhubProfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'SiteKey': siteKey,
          'DataServicesKey': dataServicesKey
        },
        body: JSON.stringify({ ContactId: contactId })
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result: BSGAPIHubProfile = await response.json();

      if (!result.Success) {
        logger.warn('PostLoginAPIhubProfile returned error', { error: result.ErrorMessage });
        return result; // Return the result so caller can read ErrorMessage
      }

      this.profile = result;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));

      // Also persist the API key separately (used by bsgAccount toggle/copy)
      if (result.APIKey) {
        sessionStorage.setItem('bsg_api_key', result.APIKey);
      }

      logger.info('BSG API Hub profile loaded', {
        apiCallsToday: result.APICallsToday,
        tokenBalance: result.TokenBalanceToday,
        activeAPIs: result.ActiveAPICount,
        status: result.APIStatus,
        recentActivity: result.RecentAPIActivity?.length ?? 0,
        transactions: result.TransactionHistory?.length ?? 0
      });

      return this.profile;
    } catch (error: any) {
      logger.error('Failed to load BSG API Hub profile', { error: error.message, stack: error.stack });
      return null;
    }
  }

  /**
   * Get the cached profile (no API call).
   * Tries in-memory first, then sessionStorage.
   */
  public getProfile(): BSGAPIHubProfile | null {
    if (this.profile) return this.profile;

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        this.profile = JSON.parse(stored);
        return this.profile;
      } catch {
        // corrupt data
      }
    }

    return null;
  }

  /**
   * Force a refresh of the profile (e.g. after regenerating API key).
   */
  public async refresh(contactId: number): Promise<BSGAPIHubProfile | null> {
    this.profile = null;
    sessionStorage.removeItem(STORAGE_KEY);
    return this.load(contactId);
  }

  /**
   * Clear cached profile (e.g. on logout).
   */
  public clear(): void {
    this.profile = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export const bsgProfileService = new BSGProfileServiceImpl();
